import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetAllProviderUsersByProviderId,
  useGetAllSponsorUserList,
  useGetParentsponsorDetails,
  useUpdateVqqVendorContacts,
  useUpdateVqqSponsorContacts,
  useResendVqqContactInvites,
  useAddProviderContact,
  useAddSponsorUser,
  useGetProviderOrgDetails,
} from "../custom-hooks/queries";
import {
  getVqqOverviewContactPermissions,
  mapOrgUsersToPickerContacts,
  resolveSavedSponsorContacts,
  resolveSavedVendorContacts,
  toRequestContactPayload,
  VQQ_MANAGER_CREATE_ROLES,
} from "./vqqOverviewContacts";
import {
  coerceHyperComplyOrgId,
  createOverviewSponsorUser,
  OVERVIEW_SPONSOR_MANAGER_PROFILE,
  refreshOverviewOrgUsers,
  resolveOverviewSponsorName,
} from "./overviewContactsShared";
import { v4 as uuidv4 } from "uuid";

/**
 * Shared VQQ Overview contact managers wiring (vendor + sponsor) for story 14534.
 * Keeps RFIDetails free of duplicated save/resend/create handlers.
 */
export function useVqqOverviewContacts({ userRole, rfisDetails, refetch, activityRefetch }) {
  const permissions = useMemo(
    () => getVqqOverviewContactPermissions(userRole, rfisDetails?.status),
    [userRole, rfisDetails?.status],
  );

  const savedVendorContacts = useMemo(
    () => resolveSavedVendorContacts(rfisDetails),
    [rfisDetails],
  );
  const savedSponsorContacts = useMemo(
    () => resolveSavedSponsorContacts(rfisDetails),
    [rfisDetails],
  );

  const providerId = rfisDetails?.provider_id ?? rfisDetails?.providerId;
  const sponsorId = rfisDetails?.sponsor_id ?? rfisDetails?.sponsorId;
  const sponsorRfiResponseId =
    rfisDetails?.sponsorRfiResponseId ?? rfisDetails?.SponsorRfiResponseId;

  const loadVendorUsers =
    Boolean(providerId) &&
    (permissions.canEditVendor || permissions.showVendorPlatformRoles);
  const loadSponsorUsers =
    Boolean(sponsorId) &&
    (permissions.canEditSponsor || permissions.showSponsorPlatformRoles);

  const { data: providerOrgDetails } = useGetProviderOrgDetails(
    permissions.canEditVendor && providerId ? providerId : null,
  );
  const vendorHyperComplyOrgId = coerceHyperComplyOrgId(providerOrgDetails?.hc_org_id);

  const { data: vendorUsersData } = useGetAllProviderUsersByProviderId(
    loadVendorUsers ? providerId : null,
    100,
    1,
    "",
  );
  const { data: sponsorUsersData } = useGetAllSponsorUserList({
    currentPage: 1,
    id: loadSponsorUsers ? sponsorId : null,
    search_text: "",
    page_size: 100,
  });
  const { data: sponsorDetails } = useGetParentsponsorDetails(
    loadSponsorUsers ? sponsorId : null,
  );

  const vendorPickerContacts = useMemo(
    () => mapOrgUsersToPickerContacts(vendorUsersData?.items ?? [], { mode: "vendor" }),
    [vendorUsersData],
  );
  const sponsorPickerContacts = useMemo(
    () => mapOrgUsersToPickerContacts(sponsorUsersData?.items ?? [], { mode: "sponsor" }),
    [sponsorUsersData],
  );

  const queryClient = useQueryClient();
  const updateVendorContactsMutation = useUpdateVqqVendorContacts();
  const updateSponsorContactsMutation = useUpdateVqqSponsorContacts();
  const resendContactInvitesMutation = useResendVqqContactInvites();
  const { mutateAsync: addProviderContact } = useAddProviderContact();
  const { mutateAsync: addSponsorUser } = useAddSponsorUser();

  const refreshAfterChange = useCallback(async () => {
    await refetch?.();
    await activityRefetch?.();
  }, [refetch, activityRefetch]);

  const saveContacts = useCallback(
    async (mode, draft) => {
      const contacts = toRequestContactPayload(draft);
      if (mode === "sponsor") {
        await updateSponsorContactsMutation.mutateAsync({
          sponsorRfiResponseId,
          contacts,
        });
      } else {
        await updateVendorContactsMutation.mutateAsync({
          sponsorRfiResponseId,
          contacts,
        });
      }
      await refreshAfterChange();
    },
    [
      sponsorRfiResponseId,
      updateSponsorContactsMutation,
      updateVendorContactsMutation,
      refreshAfterChange,
    ],
  );

  const resendInvites = useCallback(
    async (mode, targets, { resendAll = false } = {}) => {
      const emails = resendAll
        ? []
        : (targets || []).map((t) => t.email).filter(Boolean);
      await resendContactInvitesMutation.mutateAsync({
        sponsorRfiResponseId,
        contact_type: mode,
        emails,
      });
      await activityRefetch?.();
    },
    [sponsorRfiResponseId, resendContactInvitesMutation, activityRefetch],
  );

  const createUser = useCallback(
    async (mode, form) => {
      if (mode === "sponsor") {
        await createOverviewSponsorUser(addSponsorUser, {
          form,
          sponsorId,
          sponsorName: resolveOverviewSponsorName(
            rfisDetails,
            sponsorId,
            sponsorDetails,
            "vqq",
          ),
          hypercomplyOrgId: sponsorDetails?.hypercomply_org_id,
          profile: OVERVIEW_SPONSOR_MANAGER_PROFILE.vqq,
        });
      } else {
        if (!vendorHyperComplyOrgId) {
          throw new Error(
            "Could not resolve organization for this vendor. Please try again in a moment.",
          );
        }
        await addProviderContact({
          provider_user_id: uuidv4(),
          provider_id: providerId,
          organization_id: vendorHyperComplyOrgId,
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          job_title: form.job_title,
          is_primary: false,
          provider_name: rfisDetails?.vendorName || rfisDetails?.VendorName || "",
          ...VQQ_MANAGER_CREATE_ROLES.vendor,
        });
      }

      await refreshOverviewOrgUsers(
        queryClient,
        mode,
        mode === "sponsor" ? sponsorId : providerId,
      );
    },
    [
      addSponsorUser,
      addProviderContact,
      sponsorId,
      providerId,
      vendorHyperComplyOrgId,
      rfisDetails,
      sponsorDetails,
      queryClient,
    ],
  );

  return {
    permissions,
    savedVendorContacts,
    savedSponsorContacts,
    vendorPickerContacts,
    sponsorPickerContacts,
    vendorKnownEmails: (vendorUsersData?.items ?? []).map((u) => u.email),
    sponsorKnownEmails: (sponsorUsersData?.items ?? []).map((u) => u.email),
    savingVendor: updateVendorContactsMutation.isPending,
    savingSponsor: updateSponsorContactsMutation.isPending,
    saveContacts,
    resendInvites,
    createUser,
  };
}
